import React from "react";

export type StatMetricTone = "positive" | "neutral";
export type StatMetricSize = "default" | "compact";

export interface IStatMetricProps {
  label: string;
  /** Numeric display only; `$` is already prefixed when unit is currency. */
  display: string;
  /** Non-currency units render as a smaller suffix aligned to the value baseline. */
  unit?: string;
  tone?: StatMetricTone;
  size?: StatMetricSize;
  /** Hover / screen-reader explanation, shown as an (i) after the label. */
  hint?: string;
  /**
   * The figure runs on a default or fallback value (OI-04). Shown as an orange (!) after the label, with this text
   * as its tooltip, so a degraded number reads as a minor alert at a glance instead of a footnote nobody reads.
   */
  alert?: string;
}

export const StatMetric = (props: IStatMetricProps) => {
  const toneClass =
    props.tone === "positive" ? "text-zeos-green" : "text-black";
  const showUnitSuffix = props.unit && props.unit !== "$";
  const valueSizeClass =
    props.size === "compact" ? "text-3xl" : "text-3xl sm:text-4xl";
  const marginClass =
    props.size === "compact" ? "mb-0.5" : "mb-2";

  return (
    <div className="flex flex-col items-center justify-center text-center w-full px-1 leading-none">
      <p
        className={`font-semibold ${valueSizeClass} ${toneClass} inline-flex items-baseline ${marginClass}`}
      >
        <span>{props.display}</span>
        {showUnitSuffix ? (
          <span className={`ml-1 text-sm font-semibold text-black`}>
            {props.unit}
          </span>
        ) : null}
      </p>
      <p className="text-sm sm:text-base text-black leading-tight m-0 pt-0">
        {props.label}
        {props.hint ? (
          <span
            title={props.hint}
            aria-label={props.hint}
            role="img"
            data-testid="stat-metric-hint"
            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full border border-black/40 text-[10px] leading-none text-black/60 align-middle cursor-help"
          >
            i
          </span>
        ) : null}
        {props.alert ? (
          <span
            title={props.alert}
            aria-label={props.alert}
            role="img"
            tabIndex={0}
            data-testid="stat-metric-alert"
            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-zeos-orange-dk text-white text-[11px] font-bold leading-none align-middle cursor-help"
          >
            !
          </span>
        ) : null}
      </p>
    </div>
  );
};
